import { logout401Function } from "@/modules/auth/hooks/useLogout"
import { LoggerService } from "@/modules/common/logger/loggerService"
import { LoggerType } from "@/modules/utils/loggerUtils"

/**
 * Maximum number of consecutive failed token-refresh attempts tolerated before
 * the user is forcibly logged out.
 *
 * Cognito refresh tokens can expire or be revoked. Rather than disconnecting the
 * user on the very first failure — which can be a transient hiccup — we give the
 * refresh flow a few attempts and only force a logout once this threshold is
 * reached. The counter is reset on any successful token retrieval.
 */
export const MAX_REFRESH_TOKEN_ATTEMPTS = 3

let failedRefreshAttempts = 0
// Guards against triggering several concurrent logouts when many requests fail
// their refresh at the same time. Stays true until a successful token resets it.
let logoutInProgress = false

/** Current number of consecutive failed refresh attempts (mainly for tests/diagnostics). */
export const getFailedRefreshAttempts = () => failedRefreshAttempts

/**
 * Reset the failed-refresh counter. Called whenever a valid token is obtained
 * (successful refresh or fresh login) so the next session starts from scratch.
 */
export const resetFailedRefreshAttempts = () => {
  if (failedRefreshAttempts !== 0 || logoutInProgress) {
    console.log(LoggerType.Auth + `[Cognito] refresh token attempts reset (was ${String(failedRefreshAttempts)})`)
  }
  failedRefreshAttempts = 0
  logoutInProgress = false
}

/**
 * Record a failed refresh-token attempt. Once {@link MAX_REFRESH_TOKEN_ATTEMPTS}
 * consecutive failures are reached, the user is forcibly logged out and the
 * counter is locked until {@link resetFailedRefreshAttempts} is called.
 *
 * @returns true once the threshold has been reached (logout triggered or already in progress).
 */
export const registerFailedRefreshAttempt = async (): Promise<boolean> => {
  // A logout is already underway — ignore further failures until the session resets.
  if (logoutInProgress) {
    return true
  }

  failedRefreshAttempts += 1

  console.warn(
    LoggerType.Auth +
      `[Cognito] refresh token attempt failed ❌ (${String(failedRefreshAttempts)}/${String(MAX_REFRESH_TOKEN_ATTEMPTS)})`,
  )

  if (failedRefreshAttempts < MAX_REFRESH_TOKEN_ATTEMPTS) {
    return false
  }

  logoutInProgress = true

  console.error(
    LoggerType.Auth +
      `[Cognito] refresh token attempts limit reached (${String(MAX_REFRESH_TOKEN_ATTEMPTS)}) — forcing logout 🚪`,
  )
  LoggerService.captureException(
    LoggerType.Auth + "[Cognito] refresh token attempts limit reached — forcing logout",
    new Error(`Token refresh failed ${String(MAX_REFRESH_TOKEN_ATTEMPTS)} consecutive times`),
  )

  await logout401Function()

  return true
}
